import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ClipboardList, Edit3, Eye, IndianRupee, Package, Plus,
  RefreshCw, Search, ShieldCheck, Truck, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const blank = {
  name: '', generic_name: '', brand_name: '', strength: '', dosage_form: 'Tablet',
  pack_size: '', mrp: '', currency: 'INR', prescription_required: false, otc_verified: false,
  manufacturer: '', composition: '', aliases: '', search_keywords: '',
  image_url: '', description: '', active: true, schedule_metadata: '',
};

const statusLabels = {
  searching_pharmacy: 'Finding pharmacy',
  pharmacy_accepted: 'Pharmacy accepted',
  pharmacy_confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready_for_pickup: 'Ready for pickup',
  ranger_searching: 'Finding Ranger',
  ranger_assigned: 'Ranger assigned',
  out_for_delivery: 'Out for delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pharmacy_unavailable: 'Pharmacy unavailable',
  delivery_failed: 'Ranger unavailable',
  refund_pending: 'Refund pending',
};

function money(v) { return Number(v || 0).toFixed(2); }

function statusClass(status) {
  if (['completed'].includes(status)) return 'admin-status success';
  if (['cancelled', 'pharmacy_unavailable', 'delivery_failed', 'refund_pending'].includes(status)) return 'admin-status danger';
  if (['searching_pharmacy', 'ranger_searching'].includes(status)) return 'admin-status warning';
  return 'admin-status info';
}

export default function AdminPharmacy() {
  const [tab, setTab] = useState('overview');
  const [metrics, setMetrics] = useState({ counts: {} });
  const [orders, setOrders] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [providers, setProviders] = useState([]);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [catalogueImporting, setCatalogueImporting] = useState(false);
  const [catalogueFilter, setCatalogueFilter] = useState('all');
  const [detailLoading, setDetailLoading] = useState(false);
  const [pharmacySettings, setPharmacySettings] = useState({ enabled: false, pricing_complete: false, pricing_incomplete_reasons: [] });
  const [loadErrors, setLoadErrors] = useState([]);
  const [liveUpdating, setLiveUpdating] = useState(false);

  const load = async () => {
    setLoading(true);
    const entries = [
      ['metrics', api.get('/admin/pharmacy/metrics')],
      ['orders', api.get('/admin/pharmacy/orders')],
      ['providers', api.get('/admin/pharmacy/providers')],
      ['medicines', api.get('/admin/pharmacy/medicines', { params: { q } })],
      ['settings', api.get('/admin/pharmacy/settings')],
    ];
    const results = await Promise.allSettled(entries.map(([, request]) => request));
    const errors = [];
    results.forEach((result, index) => {
      const key = entries[index][0];
      if (result.status === 'rejected') {
        errors.push({ key, message: result.reason?.response?.data?.detail || `Could not load ${key}` });
        return;
      }
      const data = result.value.data || {};
      if (key === 'metrics') setMetrics(data);
      if (key === 'orders') setOrders(data.orders || []);
      if (key === 'providers') setProviders(data.providers || []);
      if (key === 'medicines') setMedicines(data.medicines || []);
      if (key === 'settings') setPharmacySettings(data);
    });
    setLoadErrors(errors);
    if (errors.length) toast.error(errors.length === 1 ? errors[0].message : 'Some Pharmacy controls could not be loaded');
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get('/admin/pharmacy/medicines', { params: { q } })
        .then((r) => setMedicines(r.data.medicines || []))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const activeMedicines = useMemo(() => medicines.filter((m) => m.active).length, [medicines]);
  const rxMedicines = useMemo(() => medicines.filter((m) => m.active && m.prescription_required).length, [medicines]);
  const filteredMedicines = useMemo(() => medicines.filter((m) => (
    catalogueFilter === 'all' ? true :
    catalogueFilter === 'otc' ? (!m.prescription_required && m.otc_verified) :
    catalogueFilter === 'rx' ? m.prescription_required :
    !m.otc_verified
  )), [medicines, catalogueFilter]);

  const resetForm = () => {
    setForm(blank);
    setEditingId(null);
    setCreating(false);
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setCreating(true);
    setForm({
      ...blank,
      ...m,
      aliases: (m.aliases || []).join(', '),
      search_keywords: (m.search_keywords || []).join(', '),
      schedule_metadata: m.schedule_metadata ? JSON.stringify(m.schedule_metadata) : '',
    });
    setTab('catalogue');
  };

  const importRealCatalogue = async () => {
    setCatalogueImporting(true);
    try {
      const { data } = await api.post('/admin/pharmacy/catalogue/import?limit=2000');
      toast.success('Imported ' + data.inserted + ' real medicine records');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Catalogue import failed');
    } finally {
      setCatalogueImporting(false);
    }
  };

  const submitMedicine = async () => {
    if (!form.name.trim() || form.mrp === '') return toast.error('Medicine name and MRP are required');
    let schedule_metadata = undefined;
    if (form.schedule_metadata.trim()) {
      try { schedule_metadata = JSON.parse(form.schedule_metadata); }
      catch { return toast.error('Schedule metadata must be valid JSON'); }
    }
    const payload = {
      ...form,
      mrp: Number(form.mrp),
      aliases: form.aliases.split(',').map((x) => x.trim()).filter(Boolean),
      search_keywords: form.search_keywords.split(',').map((x) => x.trim()).filter(Boolean),
      schedule_metadata,
    };
    delete payload.schedule_metadata_raw;
    try {
      if (editingId) {
        await api.patch('/admin/pharmacy/medicines/' + editingId, payload);
        toast.success('Medicine updated');
      } else {
        await api.post('/admin/pharmacy/medicines', payload);
        toast.success('Medicine added');
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save medicine');
    }
  };

  const openOrder = async (id) => {
    setSelectedOrder(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get('/admin/pharmacy/orders/' + id);
      setSelectedOrder(data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load order detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const count = (s) => metrics.counts?.[s] || 0;

  const togglePharmacyLive = async () => {
    setLiveUpdating(true);
    try {
      const { data } = await api.patch('/admin/pharmacy/settings', {
        enabled: !Boolean(pharmacySettings.enabled),
      });
      setPharmacySettings((current) => ({
        ...current,
        enabled: Boolean(data.enabled),
        operation_mode: data.operation_mode,
        accepting_orders: Boolean(data.enabled) && Boolean(current.pricing_complete),
      }));
      toast.success(data.enabled ? 'Pharmacy is now live for new orders' : 'Pharmacy is offline for new orders');
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || 'Could not update Pharmacy live status';
      toast.error(message);
    } finally {
      setLiveUpdating(false);
    }
  };

  return (
    <div className="admin-page" data-testid="admin-pharmacy">
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow"><Package size={13} /> Pharmacy operations</div>
          <h1>Pharmacy Control Centre</h1>
          <p>Run the catalogue, pricing, pharmacy network, live orders and delivery exceptions from one place.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/admin/pharmacy/pricing" className="admin-lux-button secondary"><IndianRupee size={16} /> Pricing & Dispatch</Link>
          <button className="admin-lux-button secondary" onClick={load}><RefreshCw size={16} /> Refresh</button>
        </div>
      </div>

      <div className="admin-card mb-5 border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="admin-eyebrow"><ShieldCheck size={13} /> Operational status</div>
            <h2 className="text-lg font-bold mt-1">{pharmacySettings.accepting_orders ? 'Pharmacy is Live' : 'Pharmacy intake is Offline'}</h2>
            <p className="text-xs text-slate-500 mt-1">
              {pharmacySettings.accepting_orders
                ? 'Customers can review and place Pharmacy orders.'
                : !pharmacySettings.pricing_complete
                  ? 'Complete and save Pharmacy pricing before opening intake.'
                  : pharmacySettings.enabled
                    ? 'Pharmacy is enabled but intake is not ready yet.'
                    : 'New Pharmacy orders are paused. Cart review remains available to customers.'}
            </p>
            {!pharmacySettings.pricing_complete && pharmacySettings.pricing_incomplete_reasons?.length > 0 && (
              <div className="mt-2 text-xs text-amber-700">{pharmacySettings.pricing_incomplete_reasons.join(' · ')}</div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              className="admin-lux-button"
              onClick={togglePharmacyLive}
              disabled={liveUpdating || (!pharmacySettings.enabled && !pharmacySettings.pricing_complete)}
            >
              {liveUpdating ? 'Updating…' : pharmacySettings.enabled ? 'Turn Pharmacy Offline' : 'Turn Pharmacy Live'}
            </button>
            <Link to="/admin/pharmacy/pricing" className="admin-lux-button secondary">
              {pharmacySettings.pricing_complete ? 'Open Pharmacy controls' : 'Fix Pharmacy setup'}
            </Link>
          </div>
        </div>
      </div>{loadErrors.length > 0 && <div className="admin-card mb-5 border-amber-200 bg-amber-50/50"><div className="flex items-start gap-3"><AlertTriangle className="text-amber-600 mt-0.5" size={18} /><div><div className="font-semibold text-amber-900">Some controls are unavailable</div><div className="text-xs text-amber-800 mt-1">{loadErrors.map((item) => <div key={item.key}><strong>{item.key}:</strong> {String(item.message)}</div>)}</div></div></div></div>}<div className="admin-dashboard-grid mb-5">
        <div className="admin-stat-card"><span>Live orders</span><strong>{metrics.total || 0}</strong><small>all tracked states</small></div>
        <div className="admin-stat-card"><span>Catalogue</span><strong>{metrics.catalogue_total ?? medicines.length}</strong><small>{metrics.catalogue_active ?? activeMedicines} active · {metrics.catalogue_prescription ?? rxMedicines} Rx</small></div>
        <div className="admin-stat-card"><span>Pharmacy network</span><strong>{metrics.approved_pharmacy_providers ?? 0}</strong><small>approved providers</small></div>
        <div className="admin-stat-card"><span>Exceptions</span><strong>{count('pharmacy_unavailable') + count('delivery_failed') + count('refund_pending')}</strong><small>need attention</small></div>
      </div>

      <div className="flex gap-2 flex-wrap mb-5">
        {[
          ['overview', 'Overview'],
          ['orders', 'Orders'],
          ['catalogue', 'Medicine Catalogue'],
          ['providers', 'Pharmacy Network'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} className={tab === key ? 'admin-lux-button' : 'admin-lux-button secondary'}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-5">
          <div className="admin-dashboard-grid">
            {[
              ['Finding pharmacy', 'searching_pharmacy'],
              ['Accepted', 'pharmacy_accepted'],
              ['Preparing', 'preparing'],
              ['Ready pickup', 'ready_for_pickup'],
              ['Finding Ranger', 'ranger_searching'],
              ['Ranger active', 'ranger_assigned'],
              ['Out for delivery', 'out_for_delivery'],
              ['Completed', 'completed'],
            ].map(([label, key]) => (
              <div className="admin-stat-card" key={key}><span>{label}</span><strong>{count(key)}</strong><small>orders</small></div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="admin-card">
              <div className="admin-card-head"><div><h2>Exception queue</h2><p>Anything that can block the customer journey.</p></div><AlertTriangle size={20} /></div>
              <div className="space-y-2">
                {[
                  ['Pharmacy unavailable', 'pharmacy_unavailable', 'Try another pharmacy matching wave.'],
                  ['Ranger unavailable', 'delivery_failed', 'Re-dispatch to another Ranger.'],
                  ['Refund pending', 'refund_pending', 'Payment recovery needs verification.'],
                ].map(([label, key, help]) => (
                  <button key={key} onClick={() => { setTab('orders'); }} className="w-full text-left border rounded-xl p-3 hover:bg-slate-50 flex items-center justify-between gap-3">
                    <div><div className="font-semibold text-sm">{label}</div><div className="text-xs text-slate-500 mt-1">{help}</div></div>
                    <span className={statusClass(key)}>{count(key)}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-head"><div><h2>Operator controls</h2><p>Commercial rules and operational switches are separated on purpose.</p></div><ShieldCheck size={20} /></div>
              <div className="space-y-2">
                <Link to="/admin/services/pharmacy/pricing" className="block border rounded-xl p-3 hover:bg-slate-50">
                  <div className="font-semibold text-sm">Pricing & dispatch</div>
                  <div className="text-xs text-slate-500 mt-1">Delivery fee, platform fee, GST, commission, Ranger payout, limits and search radius.</div>
                </Link>
                <button onClick={() => setTab('catalogue')} className="w-full text-left border rounded-xl p-3 hover:bg-slate-50">
                  <div className="font-semibold text-sm">Medicine catalogue</div>
                  <div className="text-xs text-slate-500 mt-1">Edit MRP, Rx requirement, composition, search terms, description and activation.</div>
                </button>
                <button onClick={() => setTab('providers')} className="w-full text-left border rounded-xl p-3 hover:bg-slate-50">
                  <div className="font-semibold text-sm">Pharmacy network</div>
                  <div className="text-xs text-slate-500 mt-1">See approval, availability and provider references before orders are dispatched.</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'orders' && (
        <div className="admin-card">
          <div className="admin-card-head"><div><h2>Live pharmacy orders</h2><p>{orders.length} visible orders. Open any row for timeline and commercial snapshot.</p></div><ClipboardList size={20} /></div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Order</th><th>Type</th><th>Pharmacy</th><th>Ranger</th><th>Customer total</th><th>Status</th><th>Created</th><th /></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td><strong>#{o.id.slice(0, 8)}</strong><div className="text-xs text-slate-500">{o.consumer_id?.slice(0, 8) || '—'}</div></td>
                    <td>{o.order_type === 'prescription' ? 'Prescription' : 'Catalogue'}</td>
                    <td>{o.accepted_pharmacy_id?.slice(0, 8) || 'Searching'}</td>
                    <td>{o.ranger_id?.slice(0, 8) || '—'}</td>
                    <td>₹{money(o.pricing_snapshot?.total)}</td>
                    <td><span className={statusClass(o.status)}>{statusLabels[o.status] || o.status}</span></td>
                    <td>{o.created_at ? new Date(o.created_at).toLocaleString() : '—'}</td>
                    <td><button className="text-blue-700 font-semibold" onClick={() => openOrder(o.id)}><Eye size={16} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!orders.length && <div className="text-sm text-slate-500 text-center py-10">No pharmacy orders yet.</div>}
        </div>
      )}

      {tab === 'catalogue' && (
        <div className="admin-card">
          <div className="admin-card-head">
            <div><h2>Medicine catalogue</h2><p>Central product master. Customer orders keep their own MRP snapshot.</p></div>
            <div className="flex gap-2">
              <button className="admin-lux-button secondary" onClick={importRealCatalogue} disabled={catalogueImporting}><RefreshCw size={16} /> {catalogueImporting ? 'Importing…' : 'Import 2,000 real records'}</button>
              <button className="admin-lux-button" onClick={() => { setCreating(true); setEditingId(null); setForm(blank); }}><Plus size={16} /> Add medicine</button>
            </div>
          </div>

          <div className="relative mb-4"><Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" /><Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 2,000+ medicines by name, brand or salt" /></div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {[['all','All'],['otc','OTC verified'],['rx','Prescription'],['unverified','Needs OTC review']].map(([key,label]) => (
              <button key={key} onClick={() => setCatalogueFilter(key)} className={'px-3 py-1.5 rounded-full text-xs font-semibold ' + (catalogueFilter === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600')}>{label}</button>
            ))}
          </div>
          <div className="text-xs text-slate-500 mb-3">Real catalogue records keep their source MRP. Customers only see medicines you have explicitly marked OTC verified.</div>

          {creating && (
            <div className="border rounded-2xl p-4 mb-5 bg-slate-50">
              <div className="flex items-center justify-between mb-4">
                <div><div className="font-semibold">{editingId ? 'Edit medicine' : 'Add medicine'}</div><div className="text-xs text-slate-500 mt-1">Everything here feeds customer search and prescription rules.</div></div>
                <button onClick={resetForm}><X size={18} /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ['name', 'Name'], ['generic_name', 'Generic name'], ['brand_name', 'Brand name'],
                  ['strength', 'Strength'], ['dosage_form', 'Dosage form'], ['pack_size', 'Pack size'],
                  ['mrp', 'MRP'], ['manufacturer', 'Manufacturer'], ['composition', 'Composition'],
                  ['image_url', 'Image URL'],
                ].map(([key, label]) => (
                  <Input key={key} placeholder={label} value={form[key] ?? ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} type={key === 'mrp' ? 'number' : 'text'} />
                ))}
                <Input placeholder="Aliases, comma separated" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} />
                <Input placeholder="Search keywords, comma separated" value={form.search_keywords} onChange={(e) => setForm({ ...form, search_keywords: e.target.value })} />
                <textarea className="border rounded-xl p-3 text-sm md:col-span-2 min-h-24" placeholder="Customer-facing description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <textarea className="border rounded-xl p-3 text-sm md:col-span-2 min-h-20 font-mono" placeholder='Schedule metadata JSON, e.g. {"schedule":"H"}' value={form.schedule_metadata} onChange={(e) => setForm({ ...form, schedule_metadata: e.target.value })} />
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.prescription_required} onChange={(e) => setForm({ ...form, prescription_required: e.target.checked })} /> Prescription required</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.otc_verified} onChange={(e) => setForm({ ...form, otc_verified: e.target.checked })} /> OTC verified for customer shop</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Visible to customers</label>
                <div className="flex gap-2 md:col-span-2">
                  <Button onClick={submitMedicine}>{editingId ? 'Save medicine' : 'Create medicine'}</Button>
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {filteredMedicines.map((m) => (
              <div key={m.id} className="border rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-700"><Package size={18} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{m.name}</div>
                  <div className="text-xs text-slate-500">{m.generic_name || 'Generic'} · {m.brand_name || '—'} · {m.strength || '—'} · {m.pack_size || '—'}</div>
                  <div className="text-sm font-bold mt-1">₹{money(m.mrp)} · {m.prescription_required ? 'Rx' : (m.otc_verified ? 'OTC verified' : 'OTC review')} · {m.active ? 'Active' : 'Hidden'}</div>
                  <div className="text-[10px] text-slate-400 mt-1">{m.catalogue_source || 'Admin'}</div>
                </div>
                <button className="text-sm font-semibold text-blue-700" onClick={() => startEdit(m)}><Edit3 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'providers' && (
        <div className="admin-card">
          <div className="admin-card-head"><div><h2>Pharmacy network</h2><p>Only approved and operational pharmacies can receive live requests.</p></div><Users size={20} /></div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Pharmacy</th><th>City</th><th>Approval</th><th>Availability</th><th>Reference</th></tr></thead>
              <tbody>{providers.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.name || 'Unnamed'}</strong><div className="text-xs text-slate-500">@{p.email || p.phone || p.id.slice(0, 8)}</div></td>
                  <td>{p.city || '—'}</td>
                  <td>{p.approval_status}</td>
                  <td>{p.availability_status || 'offline'}</td>
                  <td>{p.reference_number || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {selectedOrder !== null && (
        <div className="fixed inset-0 z-50 bg-slate-950/30 flex justify-end" onClick={() => setSelectedOrder(null)}>
          <aside className="w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div><div className="text-xs uppercase tracking-wide text-slate-500">Order detail</div><h2 className="text-xl font-bold">#{selectedOrder.id.slice(0, 8)}</h2></div>
              <button onClick={() => setSelectedOrder(null)}><X /></button>
            </div>
            {detailLoading ? <div className="text-sm text-slate-500">Loading…</div> : (
              <div className="space-y-5">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="flex items-center justify-between"><span className={statusClass(selectedOrder.status)}>{statusLabels[selectedOrder.status] || selectedOrder.status}</span><span className="font-bold">₹{money(selectedOrder.pricing_snapshot?.total)}</span></div>
                  <div className="text-xs text-slate-500 mt-2">{selectedOrder.order_type === 'prescription' ? 'Prescription order' : 'Catalogue order'} · {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString() : '—'}</div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Commercial snapshot</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {[
                      ['Subtotal', selectedOrder.pricing_snapshot?.subtotal],
                      ['Delivery', selectedOrder.pricing_snapshot?.delivery_fee],
                      ['Platform fee', selectedOrder.pricing_snapshot?.platform_fee],
                      ['Tax', selectedOrder.pricing_snapshot?.tax],
                      ['Pharmacy commission', selectedOrder.pricing_snapshot?.pharmacy_commission],
                      ['Ranger payout', selectedOrder.pricing_snapshot?.ranger_payout],
                    ].map(([label, value]) => <div className="border rounded-xl p-3" key={label}><div className="text-xs text-slate-500">{label}</div><div className="font-semibold mt-1">₹{money(value)}</div></div>)}
                  </div>
                </div>

                {selectedOrder.pharmacy_unavailable_reason || selectedOrder.delivery_failed_reason ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <div className="font-semibold">Exception</div>
                    <div className="mt-1">{selectedOrder.pharmacy_unavailable_reason || selectedOrder.delivery_failed_reason}</div>
                  </div>
                ) : null}

                <div>
                  <h3 className="font-semibold mb-2">Order items</h3>
                  {(selectedOrder.items || []).length ? (
                    <div className="space-y-2">{selectedOrder.items.map((item) => <div key={item.id || item.medicine_id} className="flex justify-between gap-3 border-b pb-2 text-sm"><span>{item.name} × {item.quantity}</span><span>₹{money(item.line_total)}</span></div>)}</div>
                  ) : <div className="text-sm text-slate-500">Prescription order — medicine list is determined from the uploaded prescription by the pharmacy.</div>}
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Timeline</h3>
                  <div className="space-y-2">
                    {(selectedOrder.events || []).map((event) => (
                      <div key={event.id} className="border-l-2 border-slate-200 pl-3 py-1">
                        <div className="text-sm font-medium">{event.to_status || event.event}</div>
                        <div className="text-xs text-slate-500">{event.created_at ? new Date(event.created_at).toLocaleString() : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}

      {loading && <div className="text-sm text-slate-500 mt-5">Refreshing Pharmacy operations…</div>}
    </div>
  );
}
